// Rice University: public college page (#2652, batch 2), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
// Rice's cost page refuses scripted reads; its figures were read from the
// Internet Archive capture of 2026-08-18 and match the KB's 2026-10-04 read.
//  - Rice's Common Data Set 2025-2026 (CDS_2025-26_WEBSITE_Updated_8-3-2026.pdf
//    from ideas.rice.edu): C1 applicants/admits/enrollees for Fall 2025 with
//    the residency table; C21 early decision counts; G1/G5 2026-2027 costs
//    (tuition for undergraduates who entered in 2024 or later). Quirk: its C8
//    table is labeled for students applying for Fall 2026, not Fall 2027, so
//    the testing line comes from Rice's admission page.
//  - admission.rice.edu, First-Year Domestic Applicants (decision plans and
//    dates, the 2026-2027 essay prompts, testing policy, Shepherd School of
//    Music, Architecture and Art requirements, course requirements). Its
//    collapsed date tables still carry an old "2020-2021" comment in the
//    page source; the dates used here are the ones the page states in its
//    Decision Plan text, which match the Common Data Set's C21 dates.
//  - College Scorecard, school id 227757 (net price by family income). The
//    API's DEMO_KEY was over its rate limit, so the figures were read from
//    the Scorecard's own school page, which shows the same latest fields.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Rice Common Data Set 2025-2026',
        url: 'https://ideas.rice.edu/wp-content/uploads/2026/08/CDS_2025-26_WEBSITE_Updated_8-3-2026.pdf',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Rice Office of Admission, First-Year Domestic Applicants',
        url: 'https://admission.rice.edu/apply/first-year-domestic-applicants',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?227757-Rice-University',
        read: '2026-10-15',
    },
    coa: {
        label: 'Rice Financial Aid, Cost of Attendance 2026-2027',
        url: 'https://financialaid.rice.edu/cost-attendance',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'rice-university',
    kbId: 'rice_university',
    name: 'Rice University',
    shortName: 'Rice',
    location: 'Houston, TX',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Rice brand colors); decoration only.
    branding: {
        primary: '#00205B',
        secondary: '#7C7E7F',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/rice_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 36791,
        admitted: 2948,
        enrolled: 1263,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 10610, admitted: 847 },
            { group: 'Out-of-state', applied: 17390, admitted: 1769 },
            { group: 'International', applied: 8791, admitted: 332 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Rice\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025.',
        rows: [
            { plan: 'Early Decision I and II', applied: 5748, admitted: 647 },
        ],
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Some Rice programs ask for more than the Common App:',
        rows: [
            { school: 'Shepherd School of Music', requirement: 'Extra materials by area of study, with every part complete by December 1. Music applicants cannot apply Early Decision or through QuestBridge, and admitted students stay in the music program for at least one year.' },
            { school: 'Rice School of Architecture', requirement: 'A portfolio of creative work, uploaded to the Rice Admission Student Portal, plus two architecture essays.' },
            { school: 'Department of Art', requirement: 'An optional portfolio of creative work, uploaded to the Rice Admission Student Portal.' },
            { school: 'Natural sciences and engineering', requirement: 'At a minimum, trigonometry or precalculus, and both chemistry and physics (a second year of chemistry or biology can replace physics).' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 4' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Decision II', deadline: 'January 4', decision: 'Late January', binding: true },
            { plan: 'Regular Decision', deadline: 'January 4', decision: 'By April 1', binding: false },
        ],
        note: 'Shepherd School of Music applicants apply by December 1. Admitted students deposit by January 4 (Early Decision I), mid-February (Early Decision II) or May 1 (Regular Decision).',
        source: 'firstYear',
        binding: 'Early Decision is binding, for students who have picked Rice first. You may start applications elsewhere under nonbinding plans, but if Rice admits you, you withdraw them, send no new ones, and accept Rice\'s offer with an enrollment deposit.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Rice recommends that you send SAT or ACT scores if you have them, but does not require them. If you apply without scores, you still get full consideration. If you send scores, Rice superscores them. Students at U.S. high schools can type their scores into the application instead of sending official reports.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set marks the SAT or ACT as recommended, in a table labeled for students applying for fall 2026.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition (students who entered in 2024 or later)', amount: 71140 },
            { item: 'Mandatory fees', amount: 984 },
            { item: 'Food and housing (on campus)', amount: 20530 },
            { item: 'Books and supplies', amount: 1540 },
            { item: 'Personal expenses', amount: 3115 },
        ],
        total: 97309,
        note: 'Undergraduates living on campus. First-years are also billed a one-time $370 orientation fee.',
        source: 'coa',
        netPrice: {
            average: 11164,
            byIncome: [
                { income: '$0 to $30,000', amount: 5707 },
                { income: '$30,001 to $48,000', amount: 1524 },
                { income: '$48,001 to $75,000', amount: 5420 },
                { income: '$75,001 to $110,000', amount: 14522 },
                { income: 'Over $110,000', amount: 40188 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Rice\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
